import { type ReviewChecklistItem } from "@mg/schemas/src/christo/askPuntt";
import {
  CaretDown,
  CheckCircle,
  CircleNotch,
  ClockCountdown,
} from "@phosphor-icons/react";
import { Flex, Text } from "@radix-ui/themes";
import cx from "classnames";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  STATUS_DONE_ICON_CLASS,
  STATUS_PANEL_ITEMS_LIGHT_CLASS,
  STATUS_PANEL_TOGGLE_LIGHT_CLASS,
  STATUS_PANEL_TOGGLE_MUTED_CLASS,
} from "./statusPanelTokens";

import { interactiveSurfaceClasses } from "../../../components/InteractiveSurface";

interface ChecklistItem {
  id: string;
  label: string;
  sub: string;
  ticketLabel?: string;
}

// Canonical per-ticket checks used for project (multi-ticket) reviews where
// every ticket runs through the same set of checks in parallel.
export const TICKET_REVIEW_CHECKS: { labelKey: string; subKey: string }[] = [
  { labelKey: "brandGuidelines", subKey: "visualStandards" },
  { labelKey: "visualQuality", subKey: "compositionImagery" },
  { labelKey: "contentReview", subKey: "copyClaimsMessaging" },
  { labelKey: "technicalSpecifications", subKey: "fileFormat" },
];

interface StaticCheck {
  id: string;
  labelKey: string;
  subKey: string;
}

const CHECKLIST_BY_WORKFLOW: Record<string, StaticCheck[]> = {
  artwork_review: [
    { id: "file_build", labelKey: "fileBuild", subKey: "dielinesFonts" },
    {
      id: "design_layout",
      labelKey: "designAndLayout",
      subKey: "logoBrandBarcode",
    },
    {
      id: "content_claims",
      labelKey: "contentAndClaims",
      subKey: "ingredientsRegulatory",
    },
    {
      id: "master_comparison",
      labelKey: "masterComparison",
      subKey: "alignmentReferenceSpec",
    },
  ],
  compliance_review: [
    {
      id: "regulatory",
      labelKey: "regulatoryCompliance",
      subKey: "euUkRegulations",
    },
    { id: "claim_accuracy", labelKey: "claimAccuracy", subKey: "evidenceBase" },
    {
      id: "market_rules",
      labelKey: "marketSpecificRules",
      subKey: "regionalRequirements",
    },
    {
      id: "enforcement",
      labelKey: "enforcementRisk",
      subKey: "asaFsaPrecedent",
    },
  ],
  compare: [
    {
      id: "design_align",
      labelKey: "designAlignment",
      subKey: "layoutColorsTypography",
    },
    {
      id: "content_match",
      labelKey: "contentAccuracy",
      subKey: "copyClaimsSpecs",
    },
    {
      id: "brand_consistency",
      labelKey: "brandConsistency",
      subKey: "logoUsageColorPalette",
    },
    {
      id: "spec_compliance",
      labelKey: "specificationCompliance",
      subKey: "dimensionsMaterials",
    },
  ],
  review: [
    { id: "brand", labelKey: "brandGuidelines", subKey: "visualStandards" },
    {
      id: "visual_quality",
      labelKey: "visualQuality",
      subKey: "compositionImagery",
    },
    { id: "content", labelKey: "contentReview", subKey: "copyClaimsMessaging" },
    {
      id: "technical",
      labelKey: "technicalSpecifications",
      subKey: "fileFormat",
    },
  ],
};

const FALLBACK_CHECKLIST: StaticCheck[] = [
  { id: "analysis", labelKey: "analysingFiles", subKey: "readingAssets" },
  { id: "review", labelKey: "runningReview", subKey: "applyingKnowledgeBase" },
  {
    id: "findings",
    labelKey: "compilingFindings",
    subKey: "generatingFeedback",
  },
];

type DynamicChecklistItem = ReviewChecklistItem & { ticketLabel?: string };

function toChecklistItems(
  dynamic: DynamicChecklistItem[] | undefined,
  workflowType: string,
  t: (key: string) => string,
): ChecklistItem[] {
  if (dynamic && dynamic.length > 0) {
    return dynamic.map((item, idx) => ({
      id: `dynamic_${idx}`,
      label: item.label,
      sub: item.sub,
      ticketLabel: item.ticketLabel,
    }));
  }
  const defs = CHECKLIST_BY_WORKFLOW[workflowType] ?? FALLBACK_CHECKLIST;
  return defs.map((d) => ({
    id: d.id,
    label: t(d.labelKey),
    sub: t(d.subKey),
  }));
}

/** m:ss for a non-negative elapsed span; null when we can't date the start. */
export function formatElapsed(
  startedAtIso: string | null | undefined,
  now: number,
): string | null {
  if (!startedAtIso) return null;
  const startedAt = new Date(startedAtIso).getTime();
  if (Number.isNaN(startedAt)) return null;
  const seconds = Math.floor((now - startedAt) / 1000);
  if (seconds < 0) return null;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

interface ReviewChecklistProps {
  workflowType: string;
  isReviewPolling?: boolean;
  reviewComplete?: boolean;
  dynamicItems?: DynamicChecklistItem[];
  /**
   * Live phase from the server (`chat.reviewStatus`), e.g. "Reviewing 5
   * image(s) in parallel". Standard (v1) reviews have no stage bar, and the
   * checklist rows are a client-side stagger — this string is the only real
   * progress signal, so the header renders it verbatim instead of the static
   * "Review in progress". Falls back to that label when absent.
   */
  phase?: string | null;
  /**
   * ISO timestamp the current review turn started at (the user message that
   * kicked it off) — drives the elapsed clock so a long phase still visibly
   * ticks. Server-sourced so it survives a reload mid-review.
   */
  startedAt?: string | null;
  /**
   * When set, render in parallel-review mode: header reads "Reviewing N
   * tickets in parallel", items rotate via a sliding window of the most
   * recent reveals, and per-item completion follows reviewComplete only
   * (since the checks here represent in-flight reviews, not phases that
   * already finished by the time polling starts).
   */
  parallelTicketCount?: number;
}

const PARALLEL_VISIBLE_WINDOW = 5;

/** Rolling window for checklist rows (and Deep Review checks) before
 * "Show N earlier" / "Show less". Keep both surfaces on the same count. */
export const REVIEW_ITEM_VISIBLE_WINDOW = 5;

export function ReviewChecklist({
  workflowType,
  isReviewPolling = false,
  reviewComplete = false,
  dynamicItems,
  parallelTicketCount,
  phase,
  startedAt,
}: ReviewChecklistProps) {
  const { t } = useTranslation("ui");
  const items = toChecklistItems(dynamicItems, workflowType, t);
  const isParallelMode =
    typeof parallelTicketCount === "number" && parallelTicketCount > 0;

  const [manualToggle, setManualToggle] = useState<boolean | null>(null);
  const widgetOpen = manualToggle ?? !reviewComplete;
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const prevReviewCompleteRef = useRef(reviewComplete);

  // A prior manual collapse/expand must not stick when a new review cycle
  // starts (reviewComplete flips back to false for the next in-progress run).
  useEffect(() => {
    if (prevReviewCompleteRef.current && !reviewComplete) {
      setManualToggle(null);
    }
    prevReviewCompleteRef.current = reviewComplete;
  }, [reviewComplete]);

  const hasDynamic = !!(dynamicItems && dynamicItems.length > 0);
  const staggerKey = `${hasDynamic ? "dyn" : "fallback"}-${items.length}`;

  // Refs survive remounts. Store the wall-clock start time and pre-computed
  // cumulative delays so a component remount (waitingForAI→false moves the
  // element in the DOM) can resume exactly where the timer left off.
  const startTimeRef = useRef<number | null>(null);
  const staggerDelaysRef = useRef<number[]>([]);
  const prevStaggerKeyRef = useRef("");

  if (prevStaggerKeyRef.current !== staggerKey && !reviewComplete) {
    prevStaggerKeyRef.current = staggerKey;
    startTimeRef.current = Date.now();
    const delays: number[] = [0];
    for (let i = 1; i < items.length; i++) {
      delays.push(delays[i - 1] + 3000 + Math.random() * 4000);
    }
    staggerDelaysRef.current = delays;
  }

  // On remount, initialize visibleCount to however many items have already
  // elapsed — so we never flash backwards or pre-fill the box.
  const [visibleCount, setVisibleCount] = useState<number>(() => {
    if (reviewComplete) return items.length;
    const start = startTimeRef.current;
    if (start === null) return 0;
    const elapsed = Date.now() - start;
    return staggerDelaysRef.current.filter((d) => d <= elapsed).length;
  });

  useEffect(() => {
    if (reviewComplete) {
      setVisibleCount(items.length);
      return;
    }

    // Recompute how many items should be visible right now (handles remounts).
    const start = startTimeRef.current ?? Date.now();
    const delays = staggerDelaysRef.current;
    const nowCount = delays.filter((d) => start + d <= Date.now()).length;
    setVisibleCount(nowCount);

    let count = nowCount;
    let timeoutId: ReturnType<typeof setTimeout>;

    const scheduleNext = () => {
      if (count >= items.length) return;
      const remaining = start + delays[count] - Date.now();
      timeoutId = setTimeout(
        () => {
          count++;
          setVisibleCount(count);
          scheduleNext();
        },
        Math.max(0, remaining),
      );
    };

    scheduleNext();
    return () => clearTimeout(timeoutId);
  }, [staggerKey, reviewComplete, items.length]);

  // Second-resolution clock, live only while the review runs. Standard reviews
  // can sit on one phase for minutes; without a moving number the card reads as
  // frozen even though the backend is working.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!isReviewPolling || !startedAt) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isReviewPolling, startedAt]);
  const elapsedLabel = isReviewPolling ? formatElapsed(startedAt, now) : null;

  const toggleItem = (itemId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  // In parallel mode the items represent in-flight per-ticket reviews, so a
  // done check would be misleading until the whole project review completes.
  // For single-ticket flows the existing semantics still apply: items are
  // conceptual phases that are done by the time polling starts.
  const done = isParallelMode
    ? reviewComplete
    : isReviewPolling || reviewComplete;
  const visibleItems = items.slice(0, visibleCount);

  const [showAll, setShowAll] = useState(false);
  // Both modes show a rolling window of the most recent reveals so the latest
  // items always sit at the bottom and earlier items scroll off the top.
  // Single-ticket mode also exposes a "Show all" affordance so users can pop
  // open the full list once the reveal stream has gone past.
  const windowSize = isParallelMode
    ? PARALLEL_VISIBLE_WINDOW
    : REVIEW_ITEM_VISIBLE_WINDOW;
  const displayedItems =
    !isParallelMode && showAll
      ? visibleItems
      : visibleItems.slice(Math.max(0, visibleItems.length - windowSize));
  const displayWindowOffset =
    !isParallelMode && showAll
      ? 0
      : Math.max(0, visibleItems.length - windowSize);
  const hiddenEarlierCount =
    !isParallelMode && !showAll
      ? Math.max(0, visibleItems.length - REVIEW_ITEM_VISIBLE_WINDOW)
      : 0;
  const hasMore =
    !isParallelMode && visibleItems.length > REVIEW_ITEM_VISIBLE_WINDOW;

  return (
    <>
      {/* Shadow on outer wrapper; inner clips content to rounded rect */}
      <div className="rounded-xl shadow-sm">
        <div
          data-testid="review-checklist"
          className={cx(
            "overflow-hidden",
            interactiveSurfaceClasses({ expandable: true, lightBorder: true }),
          )}
        >
          {/* Header — click to collapse/expand the whole widget */}
          <button
            data-testid="review-checklist-toggle"
            aria-label={t("toggleReviewChecklist")}
            aria-expanded={widgetOpen}
            type="button"
            onClick={() =>
              setManualToggle((prev) => !(prev ?? !reviewComplete))
            }
            className={STATUS_PANEL_TOGGLE_LIGHT_CLASS}
          >
            {isReviewPolling ? (
              <span className="size-2.5 shrink-0 animate-pulse rounded-full bg-puntt-accent-9 opacity-70" />
            ) : reviewComplete ? (
              <CheckCircle
                size={16}
                weight="fill"
                className={STATUS_DONE_ICON_CLASS}
              />
            ) : (
              <CircleNotch
                size={16}
                className="animate-spin text-puntt-accent-9 motion-reduce:animate-none"
              />
            )}
            <Text
              size="2"
              weight="medium"
              className="text-puntt-neutral-gray-12"
            >
              {reviewComplete
                ? t("reviewComplete")
                : isParallelMode
                  ? parallelTicketCount === 1
                    ? t("reviewingYourAsset")
                    : t("reviewingNAssets", { n: parallelTicketCount })
                  : isReviewPolling
                    ? // The server phase IS the progress for Standard reviews;
                      // show it rather than a label that never changes.
                      (phase?.trim() ?? "") || t("reviewInProgressLower")
                    : t("reviewingYourFiles")}
            </Text>
            {isReviewPolling && (
              <Flex align="center" gap="1" className="mr-1">
                <ClockCountdown
                  size={12}
                  className="text-puntt-neutral-gray-9"
                />
                <Text
                  size="1"
                  data-testid="review-checklist-status-meta"
                  className="whitespace-nowrap text-puntt-neutral-gray-9"
                >
                  {elapsedLabel
                    ? `${elapsedLabel} · ${t("resultsOnTicket")}`
                    : t("resultsOnTicket")}
                </Text>
              </Flex>
            )}
            <CaretDown
              size={14}
              className={`ml-auto shrink-0 text-puntt-neutral-gray-12 transition-transform duration-200 ${widgetOpen ? "rotate-0" : "-rotate-90"}`}
            />
          </button>

          {/* Items — only render what's visible so the box grows as items appear */}
          {widgetOpen && displayedItems.length > 0 && (
            <div className={STATUS_PANEL_ITEMS_LIGHT_CLASS}>
              <div className="flex flex-col gap-1.5">
                {hasMore && hiddenEarlierCount > 0 && (
                  <button
                    data-testid="review-checklist-show-earlier"
                    aria-label={t("showEarlierItems")}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAll(true);
                    }}
                    className={`${STATUS_PANEL_TOGGLE_MUTED_CLASS} rounded-lg hover:bg-puntt-neutral-gray-3`}
                  >
                    <Text size="1" className="text-puntt-neutral-gray-9">
                      {t("reviewChecklist.showEarlierCount", {
                        count: hiddenEarlierCount,
                      })}
                    </Text>
                  </button>
                )}
                {displayedItems.map((item, idx) => {
                  const absoluteIdx = displayWindowOffset + idx;
                  const isLatestEntry =
                    absoluteIdx === visibleItems.length - 1 && !reviewComplete;
                  const itemExpanded = expandedIds.has(item.id);

                  return (
                    <div
                      key={item.id}
                      className={`overflow-hidden rounded-lg border border-puntt-neutral-gray-4 bg-puntt-neutral-gray-2 ${isLatestEntry ? "animate-block-fade-up" : ""}`}
                    >
                      <button
                        data-testid="review-checklist-item"
                        aria-label={t("reviewChecklist.toggleDetailsFor", {
                          label: item.label,
                        })}
                        type="button"
                        onClick={() => toggleItem(item.id)}
                        className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left transition-colors hover:bg-puntt-neutral-gray-3/90 focus-visible:bg-puntt-neutral-gray-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-puntt-accent-7"
                      >
                        {done ? (
                          <CheckCircle
                            size={16}
                            weight="fill"
                            className={`mt-0.5 shrink-0 ${STATUS_DONE_ICON_CLASS}`}
                          />
                        ) : (
                          <CircleNotch
                            size={16}
                            className="mt-0.5 shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
                            style={{ animationDuration: `${1.5 + idx * 0.3}s` }}
                          />
                        )}
                        <Flex
                          direction="column"
                          align="start"
                          className="flex-1"
                        >
                          {item.ticketLabel && (
                            <Text
                              size="1"
                              className="text-left text-puntt-neutral-gray-9"
                            >
                              {item.ticketLabel}
                            </Text>
                          )}
                          <Text
                            size="1"
                            weight="medium"
                            className={`text-left ${
                              done
                                ? "text-puntt-neutral-gray-11"
                                : "text-puntt-neutral-gray-12"
                            }`}
                          >
                            {item.label}
                          </Text>
                        </Flex>
                        <CaretDown
                          size={12}
                          className={`shrink-0 text-puntt-neutral-gray-7 transition-transform duration-150 ${itemExpanded ? "rotate-0" : "-rotate-90"}`}
                        />
                      </button>

                      {itemExpanded && (
                        <div className="border-t border-puntt-neutral-gray-4 bg-base-white p-3 pl-[2.375rem]">
                          <Text
                            as="p"
                            size="2"
                            className="leading-relaxed text-puntt-neutral-gray-12"
                          >
                            {item.sub}
                          </Text>
                        </div>
                      )}
                    </div>
                  );
                })}

                {hasMore && showAll && (
                  <button
                    data-testid="review-checklist-show-less"
                    aria-label={t("showFewerItems")}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAll(false);
                    }}
                    className={`${STATUS_PANEL_TOGGLE_MUTED_CLASS} rounded-lg hover:bg-puntt-neutral-gray-3`}
                  >
                    <Text size="1" className="text-puntt-neutral-gray-9">
                      {t("showLess")}
                    </Text>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
